import { Collection, Database, Q } from '@nozbe/watermelondb';

import { createDefaultWriteStrategy } from '../operations/createDefaultWriteStrategy';
import {
    EVENT_CONTACT_STATUS,
    EventContactModel,
} from '../../types/EventContact';
import { EventContactDBServiceOptions } from '../../types/dbService';
import { OperationContext } from '../../types/operations';
import { EventContactWriteStrategy } from '../../types/writeStrategy';
import { getLocal } from '../../utils/date';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import { prepareUpdateEventContactInvitations } from './helpers/eventContact/eventContactUpdate';
import { toPreparedDataItems } from './helpers/eventContact/common';

export type UpdateEventContactInvitationsPayload = Partial<{
    sendSMS: boolean;
    sendEmail: boolean;
    sentSmsAt: string;
    sentEmailAt: string;
}>;

/**
 * Query facade for event_contacts.
 * Bulk writes go through Event.add / Event.update / Event.deleteByID.
 * updateEventContactInvitations is a local exception with validation + ops sync.
 */
class EventContact {
    private database: Database;
    private collection: Collection<EventContactModel>;
    private table = 'event_contacts';
    private options: EventContactDBServiceOptions;
    private validation: EventContactDBServiceOptions['validation'];
    private writeStrategy: EventContactWriteStrategy;

    constructor(options: EventContactDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
        this.writeStrategy =
            options.writeStrategy ?? createDefaultWriteStrategy();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByEventId(id: string) {
        return this.collection.query(Q.where('event_id', id));
    }

    getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value));
    }

    getByContactAndStatus(contactId: string, status: EVENT_CONTACT_STATUS) {
        const yesterdayDate = getLocal().minus({ days: 1 });
        return this.collection.query(
            Q.and(Q.where('contact_id', contactId), Q.where('status', status)),
            Q.on('events', Q.where('ends_time', Q.gt(yesterdayDate.toISO()))),
        );
    }

    getByEventAndStatus(eventId: string, status: EVENT_CONTACT_STATUS) {
        const yesterdayDate = getLocal().minus({ days: 1 });
        return this.collection.query(
            Q.and(Q.where('event_id', eventId), Q.where('status', status)),
            Q.on('events', Q.where('ends_time', Q.gt(yesterdayDate.toISO()))),
        );
    }

    async fetchByParam(param: string, value: any) {
        return this.getByParam(param, value).fetch();
    }

    getByParams(params: { name: string; value: string }[]) {
        return this.collection.query(
            Q.and(...params.map((param) => Q.where(param.name, param.value))),
        );
    }

    fetchByParams(params: { name: string; value: string }[]) {
        return this.getByParams(params).fetch();
    }

    async fetchByEventIdAndContactId(eventId: string, contactId: string) {
        return this.collection
            .query(
                Q.and(
                    Q.where('event_id', eventId),
                    Q.where('contact_id', contactId),
                ),
            )
            .fetch();
    }

    async updateEventContactInvitations(
        eventId: string,
        contactId: string,
        payload: UpdateEventContactInvitationsPayload,
        options?: { operationContext?: OperationContext },
    ) {
        await this.database.write(async () => {
            const eventContactToUpdate =
                await prepareUpdateEventContactInvitations({
                    eventId,
                    contactId,
                    payload,
                    collection: this.collection,
                });

            if (!eventContactToUpdate) {
                return;
            }

            const preparedData = toPreparedDataItems(
                [eventContactToUpdate],
                'event_contacts',
                'update',
            );

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'update',
                operationContext: options?.operationContext,
            });

            this.options.logDBAction({
                message: 'Update event contact invitations',
                modelName: this.table,
                payload: eventContactToUpdate,
            });
        });
    }
}

export default EventContact;
